import isEqual from 'lodash/isEqual';
import toNumber from 'lodash/toNumber';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ForgeTextField } from '@tylertech/forge-react';
import { FilterFooter } from '../FilterFooter';
import I18n from 'common/i18n';
import { SoQLType } from 'common/types/soql';
import { getPrecision } from 'common/numbers';
import { ParameterConfiguration } from 'common/types/reportFilters';

export function ParameterTextInput({
  parameter: { overrideValue, dataType },
  onUpdate,
  onReset
}: {
  parameter: ParameterConfiguration;
  onUpdate?: (value: string) => void;
  onReset?: () => void;
}) {
  const [initValue] = useState(overrideValue);
  const [value, setValue] = useState(overrideValue);
  const [alreadyFocused, setAlreadyFocused] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Compare the parameter value returned by initial state to the current state
  // If they are the same disable apply button
  const shouldDisableApply = useMemo(() => isEqual(initValue, value), [initValue, value]);

  const getInputProps = useMemo(() => {
    if (dataType === SoQLType.SoQLTextT) {
      return {
        className: 'text-input',
        type: 'text'
      };
    } else if (dataType === SoQLType.SoQLNumberT) {
      return {
        className: 'number-input',
        type: 'number',
        step: getPrecision(toNumber(initValue))
      };
    }
  }, [initValue, dataType]);

  // Initially set focus to the input
  useEffect(() => {
    setTimeout(() => {
      if (inputRef.current && !alreadyFocused) {
        inputRef.current.focus();
        setAlreadyFocused(true);
      }
    }, 0);
  }, [inputRef, alreadyFocused]);

  return (
    <div className="filter-controls parameter">
      <div className="parameter-input-container">
        <ForgeTextField dense={true}>
          <input
            {...getInputProps}
            ref={inputRef}
            data-testid="parameter-input-value"
            aria-label={I18n.t('shared.components.filter_bar.range_filter.value')}
            value={value}
            onChange={(event) => {
              setValue(event.target.value);
            }}
          />
        </ForgeTextField>
      </div>

      <FilterFooter
        disableApplyFilter={shouldDisableApply}
        onClickApply={() => onUpdate?.(value)}
        onClickReset={onReset}
        isReadOnly={true}
      />
    </div>
  );
}
